import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getApiBaseUrl } from '../env';

import { notionImportFailureMessage } from './import-error';

export type NotionImportBatchResponse = {
  idMap: Record<string, string>;
  pagesCreated: number;
  databasesCreated: number;
  rowsInserted: number;
  failures: NotionImportItemFailure[];
};

export type NotionImportItemFailure = {
  notionId: string;
  kind: 'page' | 'database';
  message: string;
};

export type NotionImportContentBlockPayload = {
  id: string;
  parentId: string;
  type: string;
  content: Record<string, unknown>;
  position: number;
};

export type NotionImportPagePayload = {
  kind: 'page';
  notionId: string;
  parentNotionId: string | null;
  title: string;
  contentRootId?: string;
  contentBlocks?: NotionImportContentBlockPayload[];
  uploadFailures?: string[];
};

export type NotionImportDatabasePayload = {
  kind: 'database';
  notionId: string;
  parentNotionId: string | null;
  title: string;
  csvText: string;
  rowPageContent?: Array<{
    notionId: string;
    title: string;
    contentRootId?: string;
    contentBlocks?: NotionImportContentBlockPayload[];
  }>;
  uploadFailures?: string[];
};

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  const url = `${base}${path}`;
  return fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getDevAuthHeaders(),
      ...init?.headers,
    },
  });
}

async function parseApiError(res: Response, path: string): Promise<never> {
  const text = await res.text();
  throw new Error(notionImportFailureMessage(res.status, text || res.statusText, path));
}

export async function wipeWorkspaceForImport(): Promise<void> {
  const path = '/rpc/import/wipe-workspace';
  const res = await apiFetch(path, {
    method: 'POST',
    body: JSON.stringify({ confirm: true }),
  });
  if (!res.ok) {
    await parseApiError(res, path);
  }
}

export async function postNotionImportBatch(input: {
  mountParentId: string;
  idMap: Record<string, string>;
  items: Array<NotionImportPagePayload | NotionImportDatabasePayload>;
}): Promise<NotionImportBatchResponse> {
  const path = '/rpc/import/notion/batch';
  const res = await apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    await parseApiError(res, path);
  }
  return res.json() as Promise<NotionImportBatchResponse>;
}
